import { Paper, Typography } from "@mui/material";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { useGridPanelFit } from "../hooks/useGridPanelFit";
import { mesfTheme } from "@/setup/ag-grid/mesf-theme";
import {
	CHEMISTRY_ELEMENTS,
	EMPTY,
	type RollDetail,
} from "../models/RollTurnHistoryDetail";

const HEADER_HEIGHT = 28;
const ROW_HEIGHT = 26;

const chemistryTheme = mesfTheme.withParams({
	cellHorizontalPadding: 2,
	headerFontSize: 11,
	fontSize: 12,
});

const ELEMENT_NAME: Record<string, string> = {
	C: "Carbon",
	Mn: "Manganese",
	P: "Phosphorus",
	S: "Sulfur",
	Si: "Silicon",
	Cr: "Chromium",
	Ni: "Nickel",
	Mo: "Molybdenum",
	Mg: "Magnesium",
	Co: "Cobalt",
	Va: "Vanadium",
	Cu: "Copper",
	Al: "Aluminium",
	V: "Vanadium, second column kept from the legacy sheet",
	W: "Tungsten",
};

const COLUMN_DEFS: ColDef[] = CHEMISTRY_ELEMENTS.map((element) => {
	const help = `${ELEMENT_NAME[element]} content, percent by weight`;
	return {
		field: element,
		headerName: element,
		flex: 1,
		minWidth: 44,
		type: "rightAligned",
		headerTooltip: help,
		tooltipValueGetter: () => help,
		valueFormatter: (p: ValueFormatterParams) =>
			p.value == null ? EMPTY : Number(p.value).toFixed(3),
	};
});

interface Props {
	roll: RollDetail;
}

export const RollChemistryPanel = ({ roll }: Props) => {
	const { containerRef, registerApi } = useGridPanelFit();
	// Four rolls out of five carry no analysis. The row still renders, so the grid keeps a
	// single line instead of an empty-overlay hole, and the heading carries the warning.
	const hasAnalysis = useMemo(
		() =>
			CHEMISTRY_ELEMENTS.some(
				(element) => roll[element] != null && Number(roll[element]) !== 0,
			),
		[roll],
	);
	const rows = useMemo(() => [roll], [roll]);

	return (
		<Paper elevation={3} sx={{ p: 2 }}>
			<Typography
				sx={{
					color: "primary.main",
					fontWeight: 600,
					fontSize: "0.85rem",
					mb: 1,
				}}
			>
				Chemical Analysis
				{!hasAnalysis && (
					<Typography
						component="span"
						variant="caption"
						color="text.secondary"
						sx={{ ml: 1, fontWeight: 400, textTransform: "none" }}
					>
						no analysis on record for this roll
					</Typography>
				)}
			</Typography>
			{/* Fixed height, not autoHeight: AG Grid reserves 150px for an overlay it will
			    never show here, which left a hole under a single row. */}
			<div
				ref={containerRef}
				style={{ width: "100%", height: HEADER_HEIGHT + ROW_HEIGHT + 2 }}
			>
				<AgGridReact
					rowData={rows}
					rowModelType="clientSide"
					onGridReady={(e) => registerApi(e.api)}
					columnDefs={COLUMN_DEFS}
					defaultColDef={{ resizable: true, sortable: false }}
					headerHeight={HEADER_HEIGHT}
					rowHeight={ROW_HEIGHT}
					enableBrowserTooltips
					suppressContextMenu
				/>
			</div>
		</Paper>
	);
};
